/**
 * Error-resistant third party service script loader
 * Handles loading of third party service embed script with timeout, retry, and error handling
 */
export const loadThirdPartyServiceScript = (
  scriptUrl: string,
  serviceName: string
) => {
  const LOAD_TIMEOUT = 10000;
  const MAX_RETRIES = 2;

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

    if (existingScript) {
      resolve();
      return;
    }

    let retryCount = 0;

    const loadScript = () => {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.defer = true;

      const timeoutId = setTimeout(() => {
        console.warn(
          `${serviceName} script load timed out. Retrying... (${retryCount + 1}/${MAX_RETRIES})`
        );
        script.remove();
        handleScriptError();
      }, LOAD_TIMEOUT);

      script.onload = () => {
        clearTimeout(timeoutId);
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timeoutId);
        handleScriptError();
      };

      document.head.appendChild(script);
    };

    const handleScriptError = () => {
      retryCount++;
      if (retryCount <= MAX_RETRIES) {
        setTimeout(loadScript, 2000 * retryCount);
      } else {
        const error = new Error(
          `${serviceName} script failed to load after maximum retries.`
        );
        console.error(error);
        reject(error);
      }
    };

    loadScript();
  });
};

export const loadExploScript = () => {
  const EXPLO_SCRIPT_URL = "https://embed.explo.co/bundle.js";

  return loadThirdPartyServiceScript(EXPLO_SCRIPT_URL, "Explo");
};

export const loadTryChartScript = () => {
  const TRY_CHART_SCRIPT_URL = "https://cdn.trychart.com/script.js";

  return loadThirdPartyServiceScript(TRY_CHART_SCRIPT_URL, "TryChart");
};
